import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Chip, IconButton, Tooltip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Divider, Switch, FormControlLabel, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { adminClient } from '../data/adminClient';
import { schoolRegFeeOwed } from './paymentCalculations';
import { toCsv } from '../../shared/utils/csvExport';
import TestSchoolChip from '../TestSchoolChip';

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

const EMPTY_FORM = {
  schoolName: '', schoolAddress: '', schoolBoard: '',
  principalName: '', principalContact: '',
  registrantName: '', position: '', registrantEmail: '', alternateEmail: '', cellPhone: '',
  estAttendance: '', estEntries: '', paymentMethod: '', acknowledged: '', status: 'Submitted',
  eventCode: '', participantsLockOverride: '', entriesLockOverride: '',
};

// Each row: which override field/effective-lock key drives its switch, in
// the edit dialog's Manual Overrides section.
const LOCK_ROWS = [
  { key: 'participants', label: 'Participants', overrideField: 'participantsLockOverride', effectiveField: 'participantsLocked' },
  { key: 'entries', label: 'Entries', overrideField: 'entriesLockOverride', effectiveField: 'entriesLocked' },
];

// Expanded detail row under a registration: full address (street, city,
// province, postal — the backend fills fullAddress from the school's
// account) plus the school's contacts.
function RegistrationDetails({ row }) {
  const fields = [
    { label: 'Address', value: row.fullAddress || row.schoolAddress },
    { label: 'Principal', value: [row.principalName, row.principalContact].filter(Boolean).join(' · ') },
    { label: 'Registrant', value: [row.registrantName, row.position && `(${row.position})`].filter(Boolean).join(' ') },
    { label: 'Contact', value: [row.registrantEmail, row.alternateEmail, row.cellPhone].filter(Boolean).join(' · ') },
  ].filter(f => f.value);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
      {fields.map(({ label, value }) => (
        <Box key={label}>
          <Typography variant="caption" color="text.disabled" fontWeight={600} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function SchoolRegistrationsSection({ event, canEdit }) {
  const [openRows, setOpenRows] = useState(() => new Set()); // rowIndexes with details expanded
  const toggleRow = (rowIndex) => setOpenRows(prev => {
    const next = new Set(prev);
    next.has(rowIndex) ? next.delete(rowIndex) : next.add(rowIndex);
    return next;
  });
  const [rows, setRows] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [entries, setEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('schoolName');
  const [sortDir, setSortDir] = useState('asc');
  const [editDialog, setEditDialog] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteRoster, setDeleteRoster] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [promoting, setPromoting] = useState(null);
  // Resolved current lock state for whichever school is open in the edit
  // dialog, fetched once when it opens — null while loading. Read-only
  // reference for the "Auto" case: distinct from the raw override value,
  // the effective state also factors in the event-wide override and the
  // Timeline deadline (see getEffectiveLocks on the backend), so it can't
  // be derived from the override alone. Once the override is explicitly
  // 'Locked'/'Open' it wins outright (see getEffectiveLocks's precedence),
  // so the switch can reflect a locally-pending change with no refetch.
  const [effectiveLocks, setEffectiveLocks] = useState(null);
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reg, part, ent, stf, prc] = await Promise.all([
        adminClient.getEventRegistrations(),
        adminClient.getParticipants(),
        adminClient.getEntries(),
        adminClient.getStaff(),
        adminClient.getPrices(),
      ]);
      setRows(reg);
      setParticipants(part);
      setEntries(ent);
      setStaff(stf);
      // Same flatten as SchoolLookupSection.js: adminClient.getPrices() is
      // the Prices-tab row list, [{ rowIndex, item, price }] — reduce to the
      // { [item]: price } map schoolRegFeeOwed expects.
      setPrices(Object.fromEntries(prc.map(p => [p.item, p.price])));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Live registered counts per school — distinct from estAttendance/estEntries
  // above, which are just what the registrant estimated on the original form.
  const countsBySchool = useMemo(() => {
    const map = new Map();
    const bump = (schoolName, field) => {
      if (!schoolName) return;
      const entry = map.get(schoolName) || { participants: 0, entries: 0, staff: 0 };
      entry[field] += 1;
      map.set(schoolName, entry);
    };
    participants.forEach(p => bump(p.schoolName, 'participants'));
    entries.forEach(e => bump(e.schoolName, 'entries'));
    staff.forEach(s => bump(s.schoolName, 'staff'));
    return map;
  }, [participants, entries, staff]);

  // Derived straight from the same dollar amount SchoolLookupSection.js (the
  // actual budget/payment page) tracks — schoolRegFeeAmountPaid vs the
  // current School Registration Fee price — rather than the legacy feePaid
  // Yes/blank flag, which is a manually-set flag independent of any real
  // dollar amount. No more editing it from here; SchoolLookupSection is
  // where a real payment actually gets recorded.
  const augmentedRows = useMemo(() => {
    const owed = schoolRegFeeOwed(prices);
    return rows.map(r => {
      const counts = countsBySchool.get(r.schoolName) || { participants: 0, entries: 0, staff: 0 };
      const paid = r.schoolRegFeeAmountPaid || 0;
      return {
        ...r,
        participantsCount: counts.participants,
        entriesCount: counts.entries,
        staffCount: counts.staff,
        schoolFeeOwed: owed,
        schoolFeePaid: owed > 0 && paid >= owed,
        // Same rule as the backend's getPaymentClearedSchoolNames: until this
        // is true, the school's participants/entries/staff are hidden from the
        // other registration screens, so they aren't counted here either.
        paymentPending: !(r.okToProceed || owed <= 0 || paid >= owed),
      };
    });
  }, [rows, countsBySchool, prices]);

  const displayRows = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = augmentedRows.filter(r =>
      r.schoolName.toLowerCase().includes(q) ||
      r.registrantName.toLowerCase().includes(q) ||
      r.registrantEmail.toLowerCase().includes(q) ||
      (r.eventCode || '').toLowerCase().includes(q)
    );
    return [...filtered].sort((a, b) => {
      const av = String(a[sortField] || '').toLowerCase();
      const bv = String(b[sortField] || '').toLowerCase();
      const cmp = av.localeCompare(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [augmentedRows, search, sortField, sortDir]);

  // Totals reflect whatever's currently filtered/displayed, matching the
  // "X of Y registrations" caption below the table — excluding Withdrawn
  // schools, whose estimates/rosters no longer count toward anything (they
  // paid their non-refundable registration fee but aren't attending). Their
  // row still appears in the table itself, just greyed out.
  // Test schools (isTest) are visible in the table but never counted.
  // Payment-pending schools still count toward the estimates, but not the
  // registered participants/entries/staff totals.
  const totals = useMemo(() => displayRows
    .filter(r => r.status !== 'Withdrawn' && !r.isTest)
    .reduce((acc, r) => {
      const counted = !r.paymentPending;
      return {
        estAttendance: acc.estAttendance + (Number(r.estAttendance) || 0),
        estEntries: acc.estEntries + (Number(r.estEntries) || 0),
        participants: acc.participants + (counted ? r.participantsCount || 0 : 0),
        entries: acc.entries + (counted ? r.entriesCount || 0 : 0),
        staff: acc.staff + (counted ? r.staffCount || 0 : 0),
      };
    }, { estAttendance: 0, estEntries: 0, participants: 0, entries: 0, staff: 0 }), [displayRows]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const openAdd = () => {
    setFormData({ ...EMPTY_FORM });
    setError(null);
    setEditDialog({ mode: 'add' });
  };

  const openEdit = (row) => {
    setFormData({ ...EMPTY_FORM, ...row });
    setError(null);
    setEditDialog({ mode: 'edit', row });
    setEffectiveLocks(null);
    adminClient.getRegistrationLocks(row.rowIndex).then(setEffectiveLocks).catch(err => setError(err.message));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const isEdit = editDialog.mode === 'edit';
      if (isEdit) {
        const rowIndex = editDialog.row.rowIndex;
        await adminClient.updateEventRegistration(rowIndex, formData);
        // Edits never shift row position, so a local patch is safe — no
        // refetch needed. formData's eventCode is already normalized
        // client-side (see the field's onChange below) the same way the
        // backend stores it, so this matches what the server now holds.
        setRows(prev => prev.map(r => r.rowIndex === rowIndex ? { ...r, ...formData } : r));
      } else {
        // A brand-new row's server-assigned rowIndex isn't known client-side,
        // and adding a registration is rare compared to edits — a full
        // refetch here isn't worth guessing at it.
        await adminClient.createEventRegistration(formData);
        await fetchRows();
      }
      setEditDialog(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(deleteConfirm.rowIndex);
    try {
      const alsoDeleteRoster = deleteRoster && (deleteConfirm.participantsCount > 0 || deleteConfirm.entriesCount > 0);
      await adminClient.deleteEventRegistration(deleteConfirm.rowIndex, {
        deleteRoster: alsoDeleteRoster,
        expectedSchoolName: deleteConfirm.schoolName,
      });
      setDeleteConfirm(null);
      // Deleting shifts every row below it, invalidating their locally-held
      // rowIndex — refetch the registrations list so a follow-up delete
      // targets the right row. Participants/entries only changed if the
      // roster was deleted too, so only refetch them in that case.
      if (alsoDeleteRoster) {
        await fetchRows();
      } else {
        const reg = await adminClient.getEventRegistrations();
        setRows(reg);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  // Schools waitlisted at submission time (see POST /api/teacher/event-
  // registration's estAttendance-vs-maxParticipants check) or manually set
  // to 'Waitlisted' via the edit dialog's Status field — surfaced here as
  // its own section since these schools can't add participants/entries yet
  // (see isSchoolWaitlisted on the backend) and the admin needs a direct
  // way to let them in once space frees up.
  const waitlistedRows = useMemo(() => augmentedRows.filter(r => r.status === 'Waitlisted'), [augmentedRows]);

  // Each school's own registrant (the main contact who submitted the School
  // Account Registration) always belongs on both lists — they're not part
  // of the Staff tab at all, so there's no checkbox for them to begin with.
  // Withdrawn schools are skipped, same as the totals above. Shaped like a
  // staff contact (firstName/lastName/schoolName/email/phone) so the same
  // table/copy/CSV code below can treat both kinds of contact identically;
  // sourceLabel distinguishes them from a staff row's Teacher/Chaperone role.
  const schoolMainContacts = useMemo(() => rows
    .filter(r => r.status !== 'Withdrawn' && !r.isTest)
    .map(r => ({
      id: `school-${r.rowIndex}`,
      firstName: r.registrantName || r.schoolName,
      lastName: '',
      schoolName: r.schoolName,
      email: r.registrantEmail,
      phone: r.cellPhone,
      sourceLabel: 'School main contact',
    })), [rows]);

  // Built from the "Receive emails" / "Add to WhatsApp" checkboxes teachers
  // set per staff/chaperone on their own Roster page (StaffChaperones.js),
  // plus every school's main contact above — this is the admin-side place
  // to actually use those flags: one master list per channel, across every
  // school, for the Teacher/Chaperone Communication List dialog below. The main contact
  // always stays on both lists, even when they also appear as a flagged
  // staff/chaperone with the same email/phone — deliberately not deduped
  // away, since a school with a flagged teacher/chaperone should still show
  // its main contact too, not have it silently dropped as a "duplicate".
  const emailContacts = useMemo(() => {
    const staffContacts = staff.filter(s => !s.isTest && s.receiveEmails && s.email);
    const mainContacts = schoolMainContacts.filter(s => s.email);
    return [...mainContacts, ...staffContacts];
  }, [staff, schoolMainContacts]);

  const whatsappContacts = useMemo(() => {
    const staffContacts = staff.filter(s => !s.isTest && s.addToWhatsapp && s.phone);
    const mainContacts = schoolMainContacts.filter(s => s.phone);
    return [...mainContacts, ...staffContacts];
  }, [staff, schoolMainContacts]);

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(current => current === field ? '' : current), 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const handleDownloadContactsCSV = (rows, headers, filename) => {
    const csv = toCsv([headers, ...rows]);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePromote = async (row) => {
    setPromoting(row.rowIndex);
    try {
      await adminClient.updateEventRegistration(row.rowIndex, { ...row, status: 'Submitted' });
      setRows(prev => prev.map(r => r.rowIndex === row.rowIndex ? { ...r, status: 'Submitted' } : r));
    } catch (err) {
      setError(err.message);
    } finally {
      setPromoting(null);
    }
  };

  // Per-school manual override for participants/entries locks — same '' |
  // 'Locked' | 'Open' values as the event-wide override in AdminDashboard.js's
  // CapacityCard, just scoped to one school. Purely local, like every other
  // field on this form — it only actually saves when handleSave fires (see
  // registrationFields on the backend), not on every switch flip.
  const setLockOverride = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const codeInvalid = !!formData.eventCode && formData.eventCode.length !== 3;
  const codeDuplicate = formData.eventCode && formData.eventCode.length === 3
    ? rows.find(r => r.eventCode === formData.eventCode && r.rowIndex !== editDialog?.row?.rowIndex)
    : null;

  const handleExportCSV = () => {
    const headers = [
      'School Name', 'Event Code', 'School Board', 'Registrant', 'Email',
      'Est. Attendance', 'Est. Entries', 'Reg. Participants', 'Reg. Entries', 'Staff/Chaperones',
      'Payment Method', 'Status', 'Fee Paid', 'First Time', 'Needs New Plaque', 'Test School', 'Timestamp',
    ];
    const data = displayRows.map(r => [
      r.schoolName, r.eventCode, r.schoolBoard, r.registrantName, r.registrantEmail,
      r.estAttendance, r.estEntries, r.participantsCount, r.entriesCount, r.staffCount,
      r.paymentMethod, r.status, r.schoolFeePaid ? 'Paid' : 'Outstanding',
      r.firstTimeDancefest,
      // First-time schools always need one (same rule as the Plaques to order
      // printout in backend/printReports.js), even though
      // the form only asks returning schools the "plaque full" question.
      r.firstTimeDancefest === 'Yes' ? 'Yes (first time)' : r.needsNewPlaque ? 'Yes (replacement)' : 'No',
      r.isTest ? 'Yes' : '',
      r.timestamp,
    ]);
    const csv = toCsv([headers, ...data]);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `school-registrations-${event?.eventName || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Browsers don't treat "/" as a wrap point inside an otherwise-unbroken
  // run of text, so a label like "Staff/Chaperones" would only ever wrap at
  // its one real space (or not at all) — <wbr/> adds an explicit, invisible
  // break opportunity right after each slash without changing the text.
  const withSlashBreaks = (label) => label.split('/').map((part, i, arr) => (
    <React.Fragment key={i}>
      {part}{i < arr.length - 1 && <>/<wbr /></>}
    </React.Fragment>
  ));

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.3, flexShrink: 0 }} />;
    return sortDir === 'asc'
      ? <KeyboardArrowUpIcon fontSize="small" sx={{ flexShrink: 0 }} />
      : <KeyboardArrowDownIcon fontSize="small" sx={{ flexShrink: 0 }} />;
  };

  const formField = (label, key, props = {}) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={formData[key] || ''}
      onChange={e => setFormData(prev => ({ ...prev, [key]: e.target.value }))}
      {...props}
    />
  );

  const TABLE_COLS = [
    { label: 'School', field: 'schoolName' },
    { label: 'Code', field: 'eventCode' },
    { label: 'Registrant', field: 'registrantName' },
    { label: 'Est. Att.', field: 'estAttendance' },
    { label: 'Est. Entries', field: 'estEntries' },
    { label: 'Reg. Participants', field: 'participantsCount' },
    { label: 'Reg. Entries', field: 'entriesCount' },
    { label: 'Staff/Chaperones', field: 'staffCount' },
    { label: 'Payment', field: 'paymentMethod' },
    { label: 'Fee', field: 'schoolFeePaid' },
    { label: 'Status', field: 'status' },
  ];

  return (
    <>
      {waitlistedRows.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2, borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <HourglassTopIcon color="warning" fontSize="small" />
            <Typography fontWeight={600}>
              Waitlist ({waitlistedRows.length} school{waitlistedRows.length === 1 ? '' : 's'})
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            These schools registered while the event was full — they can't add participants or entries until promoted below.
          </Typography>
          <Stack spacing={1}>
            {waitlistedRows.map(row => (
              <Box key={row.rowIndex} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{row.schoolName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Est. {row.estAttendance || 0} participants · {row.registrantName}
                  </Typography>
                </Box>
                {canEdit && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handlePromote(row)}
                    disabled={promoting === row.rowIndex}
                  >
                    {promoting === row.rowIndex ? <CircularProgress size={16} /> : 'Promote off waitlist'}
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search schools..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Tooltip title="Refresh — pulls in any changes made elsewhere since this page loaded">
          <IconButton size="small" onClick={fetchRows} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExportCSV} disabled={rows.length === 0}>
          Export CSV
        </Button>
        <Button variant="outlined" size="small" startIcon={<ContactMailIcon />} onClick={() => setContactsDialogOpen(true)} disabled={rows.length === 0}>
          Teacher/Chaperone Communication List
        </Button>
        {canEdit && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>
            Add
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1, py: 0.75 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {TABLE_COLS.map(({ label, field }) => (
                  <TableCell key={field} onClick={() => handleSort(field)}
                    sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Box component="span">{withSlashBreaks(label)}</Box>
                      <SortIcon field={field} />
                    </Box>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'No matching registrations.' : 'No registrations yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow sx={{ bgcolor: 'action.selected' }}>
                  <TableCell colSpan={3} sx={{ py: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Totals ({displayRows.filter(r => !r.isTest).length} school{displayRows.filter(r => !r.isTest).length === 1 ? '' : 's'}, {`${totals.participants + totals.staff} people registered`})</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}><Typography variant="subtitle1" fontWeight={700}>{totals.estAttendance}</Typography></TableCell>
                  <TableCell sx={{ py: 2 }}><Typography variant="subtitle1" fontWeight={700}>{totals.estEntries}</Typography></TableCell>
                  <TableCell sx={{ py: 2 }}><Typography variant="subtitle1" fontWeight={700}>{totals.participants}</Typography></TableCell>
                  <TableCell sx={{ py: 2 }}><Typography variant="subtitle1" fontWeight={700}>{totals.entries}</Typography></TableCell>
                  <TableCell sx={{ py: 2 }}><Typography variant="subtitle1" fontWeight={700}>{totals.staff}</Typography></TableCell>
                  <TableCell colSpan={4} sx={{ py: 2 }} />
                </TableRow>
              )}
              {displayRows.map(row => (
                <React.Fragment key={row.rowIndex}>
                <TableRow hover sx={row.status === 'Withdrawn' ? { opacity: 0.55, bgcolor: 'action.hover' } : undefined}>
                  <TableCell>
                    <Tooltip title={openRows.has(row.rowIndex) ? 'Hide details' : 'Show address & contacts'}>
                      <IconButton size="small" onClick={() => toggleRow(row.rowIndex)} sx={{ p: 0.25, mr: 0.25, verticalAlign: 'middle' }}
                        aria-label={openRows.has(row.rowIndex) ? 'Hide details' : 'Show details'}>
                        {openRows.has(row.rowIndex) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={row.timestamp ? `Updated ${fmtDate(row.timestamp)}` : ''} placement="top" arrow disableHoverListener={!row.timestamp}>
                      <Typography variant="body2" fontWeight={500} sx={{ display: 'inline-block' }}>{row.schoolName}</Typography>
                    </Tooltip>
                    {row.isTest && <TestSchoolChip />}
                    {row.schoolBoard && <Typography variant="caption" color="text.secondary" display="block">{row.schoolBoard}</Typography>}
                  </TableCell>
                  <TableCell>
                    {row.eventCode
                      ? <Chip label={row.eventCode} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.registrantName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{row.registrantEmail}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.estAttendance}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.estEntries}</TableCell>
                  {['participantsCount', 'entriesCount', 'staffCount'].map(field => (
                    <TableCell key={field} sx={{ whiteSpace: 'nowrap', color: row.paymentPending ? 'text.disabled' : undefined }}>
                      {row.paymentPending
                        ? <Tooltip title={`${row[field]} registered — hidden and not counted until this school's registration payment is received (or OK to Proceed is on)`}><span>Pending</span></Tooltip>
                        : row[field]}
                    </TableCell>
                  ))}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.paymentMethod}</TableCell>
                  <TableCell>
                    <Tooltip title={row.schoolFeeOwed ? `${row.schoolFeePaid ? 'Fully' : 'Not fully'} paid — $${(row.schoolRegFeeAmountPaid || 0).toFixed(2)} of $${row.schoolFeeOwed.toFixed(2)}. Record payments on the School Payments page.` : 'No School Registration Fee price set'}>
                      {row.schoolFeePaid
                        ? <Chip label="Paid" color="success" size="small" />
                        : <Chip label="Outstanding" color="warning" size="small" variant="outlined" />}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {row.status === 'Waitlisted'
                      ? <Chip label="Waitlisted" color="warning" size="small" icon={<HourglassTopIcon fontSize="small" />} />
                      : row.status === 'Withdrawn'
                        ? <Chip label="Withdrawn" size="small" icon={<DoDisturbIcon fontSize="small" />} sx={{ bgcolor: 'grey.300', color: 'text.secondary' }} />
                        : <Typography variant="caption" color="text.secondary">{row.status || '—'}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      {canEdit ? (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => { setDeleteRoster(true); setDeleteConfirm(row); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                {openRows.has(row.rowIndex) && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={12} sx={{ py: 1.5, px: 2 }}>
                      <RegistrationDetails row={row} />
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {displayRows.length} of {rows.length} registrations
      </Typography>

      {/* Add / Edit dialog */}
      <Dialog open={!!editDialog} onClose={() => !saving && setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editDialog?.mode === 'add' ? 'Add Registration' : 'Edit Registration'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>School</Typography>
            {formField('School Name *', 'schoolName')}
            {formField('Event Code', 'eventCode', {
              inputProps: { maxLength: 3, style: { textTransform: 'uppercase', letterSpacing: 1 } },
              onChange: e => setFormData(prev => ({ ...prev, eventCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3) })),
              error: codeInvalid,
              helperText: codeInvalid
                ? 'Must be exactly 3 characters'
                : codeDuplicate
                  ? `Already used by ${codeDuplicate.schoolName}`
                  : "Manually assigned, for this event only — separate from the school's login ID",
            })}
            {formField('School Address', 'schoolAddress')}
            {formField('School Board', 'schoolBoard')}
            {formField('Principal Name', 'principalName')}
            {formField('Principal Contact', 'principalContact')}

            <Divider />
            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Registrant</Typography>
            {formField('Registrant Name', 'registrantName')}
            {formField('Position', 'position')}
            {formField('Registrant Email', 'registrantEmail', { type: 'email' })}
            {formField('Alternate Email', 'alternateEmail', { type: 'email' })}
            {formField('Cell Phone', 'cellPhone')}

            <Divider />
            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Event Registration</Typography>
            {formField('Est. Attendance', 'estAttendance', { type: 'number', inputProps: { min: 0 } })}
            {formField('Est. Entries', 'estEntries', { type: 'number', inputProps: { min: 0 } })}
            <FormControl size="small" fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select value={formData.paymentMethod || ''} label="Payment Method"
                onChange={e => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}>
                <MenuItem value="Invoice">Invoice Required</MenuItem>
                <MenuItem value="Cheque">Cheque</MenuItem>
                <MenuItem value="E-Transfer">E-Transfer</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={formData.status || ''} label="Status"
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}>
                <MenuItem value="Submitted">Submitted</MenuItem>
                <MenuItem value="Waitlisted">Waitlisted</MenuItem>
                <MenuItem value="Withdrawn">Withdrawn</MenuItem>
              </Select>
            </FormControl>

            {editDialog?.mode === 'edit' && (
              <>
                <Divider />
                <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Manual Overrides
                </Typography>
                {LOCK_ROWS.map(({ key, label, overrideField, effectiveField }) => {
                  const override = formData[overrideField] || '';
                  // 'Locked'/'Open' are unambiguous locally now that the
                  // school-level override always wins (see getEffectiveLocks) —
                  // only 'Auto' needs the snapshot fetched when the dialog
                  // opened, since that depends on the event-wide override /
                  // Timeline deadline, which aren't edited on this form.
                  const effective = override === 'Locked' ? true : override === 'Open' ? false : effectiveLocks?.[effectiveField];
                  return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography variant="body2">{label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {override === '' ? 'Auto' : `Manual override: ${override}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {override !== '' && (
                          <Button size="small" onClick={() => setLockOverride(overrideField, '')}>
                            Reset to auto
                          </Button>
                        )}
                        <FormControlLabel
                          sx={{ ml: 0 }}
                          labelPlacement="start"
                          control={
                            <Switch
                              size="small"
                              color="warning"
                              checked={!!effective}
                              disabled={effectiveLocks === null}
                              onChange={e => setLockOverride(overrideField, e.target.checked ? 'Locked' : 'Open')}
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight={600} color={effectiveLocks === null ? 'text.secondary' : effective ? 'warning.main' : 'success.main'} sx={{ minWidth: 56 }}>
                              {effectiveLocks === null ? '…' : effective ? 'Locked' : 'Unlocked'}
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !formData.schoolName || codeInvalid}>
            {saving ? <CircularProgress size={18} color="inherit" /> : editDialog?.mode === 'add' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Registration</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteConfirm?.schoolName}</strong> from event registrations? This cannot be undone.
          </Typography>
          {(deleteConfirm?.participantsCount > 0 || deleteConfirm?.entriesCount > 0) && (
            <>
              <FormControlLabel
                sx={{ mt: 2, alignItems: 'flex-start' }}
                control={<Checkbox checked={deleteRoster} onChange={(e) => setDeleteRoster(e.target.checked)} disabled={!!deleting} sx={{ pt: 0.5 }} />}
                label={
                  <Typography variant="body2">
                    Also delete this school's <strong>{deleteConfirm.participantsCount}</strong> participant{deleteConfirm.participantsCount === 1 ? '' : 's'} and <strong>{deleteConfirm.entriesCount}</strong> entr{deleteConfirm.entriesCount === 1 ? 'y' : 'ies'} (including uploaded music)
                  </Typography>
                }
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Fee payment records are kept either way.{!deleteRoster && ' If unchecked, the participants and entries stay in the sheet without a registration.'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={!!deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={!!deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Communication lists — each school's main contact plus any staff/
          chaperone flagged "Receive emails" / "Add to WhatsApp" on the
          teacher's Roster page. */}
      <Dialog open={contactsDialogOpen} onClose={() => setContactsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Teacher/Chaperone Communication List</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Includes each school's main registration contact automatically, plus any staff/chaperone flagged "Receive emails" / "Add to WhatsApp" on their Roster page.
          </Typography>

          {[
            {
              key: 'email',
              title: 'Email list',
              contacts: emailContacts,
              value: (s) => s.email,
              copyLabel: 'Copy emails',
              emptyText: 'No email contacts found.',
              csvHeaders: ['Name', 'Role', 'School', 'Email'],
              csvFilename: 'dancefest-email-list.csv',
            },
            {
              key: 'whatsapp',
              title: 'WhatsApp numbers',
              contacts: whatsappContacts,
              value: (s) => s.phone,
              copyLabel: 'Copy numbers',
              emptyText: 'No WhatsApp contacts found.',
              csvHeaders: ['Name', 'Role', 'School', 'Phone'],
              csvFilename: 'dancefest-whatsapp-list.csv',
            },
          ].map(({ key, title, contacts, value, copyLabel, emptyText, csvHeaders, csvFilename }, i) => (
            <Box key={key} sx={{ mb: i === 0 ? 3 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography fontWeight={600}>{title} ({contacts.length})</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    disabled={contacts.length === 0}
                    onClick={() => handleCopy(contacts.map(value).join(', '), key)}
                  >
                    {copiedField === key ? 'Copied!' : copyLabel}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon fontSize="small" />}
                    disabled={contacts.length === 0}
                    onClick={() => handleDownloadContactsCSV(
                      contacts.map(s => [`${s.firstName} ${s.lastName}`.trim(), s.sourceLabel || s.role || '', s.schoolName, value(s)]),
                      csvHeaders,
                      csvFilename,
                    )}
                  >
                    CSV
                  </Button>
                </Stack>
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 160, overflow: 'auto', p: contacts.length === 0 ? 1.5 : 0 }}>
                {contacts.length === 0 ? (
                  <Typography variant="caption" color="text.disabled">{emptyText}</Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {contacts.map(s => (
                        <TableRow key={s.id}>
                          <TableCell sx={{ border: 0, py: 0.5 }}>
                            <Typography variant="body2">{s.firstName} {s.lastName}</Typography>
                            {(s.sourceLabel || s.role) && (
                              <Typography variant="caption" color="text.disabled" display="block">{s.sourceLabel || s.role}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{s.schoolName}</Typography>
                          </TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }}>
                            <Typography variant="body2">{value(s)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
